import React, { useMemo, useState } from 'react';
import NavBar from './NavBar.js';
import Footer from './Footer.js';
import { useFaqItems } from '../../ViewModel/FAQViewModel.js';
import '../styles/FAQ.css';

const EMPTY_DRAFT = {
  question: '',
  answer: '',
  sortOrder: 0,
  isActive: true,
};

function FAQ({ adminMode = false, onAdminLogout = () => {}, isAdminLoggedIn = false }) {
  const { items, loading, error, createFaq, updateFaq, deleteFaq } = useFaqItems();
  const [expandedId, setExpandedId] = useState(null);
  const [status, setStatus] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [createDraft, setCreateDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_DRAFT);

  const visibleItems = useMemo(() => {
    const source = adminMode ? items : items.filter((item) => item.isActive);
    return source.slice().sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  }, [items, adminMode]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createDraft.question.trim() || !createDraft.answer.trim()) {
      setStatus('Fråga och svar måste fyllas i.');
      return;
    }

    setCreating(true);
    try {
      await createFaq({
        question: createDraft.question.trim(),
        answer: createDraft.answer.trim(),
        sortOrder: Number(createDraft.sortOrder) || 0,
        isActive: createDraft.isActive,
      });
      setCreateDraft(EMPTY_DRAFT);
      setStatus('FAQ skapad.');
    } catch (err) {
      setStatus(`Kunde inte skapa FAQ: ${err.message}`);
    } finally {
      setCreating(false);
      setTimeout(() => setStatus(null), 1800);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({
      question: item.question,
      answer: item.answer,
      sortOrder: item.sortOrder,
      isActive: item.isActive,
    });
  };

  const saveEdit = async (id) => {
    if (!editDraft.question.trim() || !editDraft.answer.trim()) {
      setStatus('Fråga och svar måste fyllas i.');
      return;
    }

    setUpdatingId(id);
    try {
      await updateFaq(id, {
        question: editDraft.question.trim(),
        answer: editDraft.answer.trim(),
        sortOrder: Number(editDraft.sortOrder) || 0,
        isActive: editDraft.isActive,
      });
      setEditingId(null);
      setStatus('FAQ uppdaterad.');
    } catch (err) {
      setStatus(`Kunde inte uppdatera FAQ: ${err.message}`);
    } finally {
      setUpdatingId(null);
      setTimeout(() => setStatus(null), 1800);
    }
  };

  const removeItem = async (id) => {
    const shouldDelete = window.confirm('Ta bort den här FAQ-frågan?');
    if (!shouldDelete) return;

    setDeletingId(id);
    try {
      await deleteFaq(id);
      if (expandedId === id) setExpandedId(null);
      if (editingId === id) setEditingId(null);
      setStatus('FAQ borttagen.');
    } catch (err) {
      setStatus(`Kunde inte ta bort FAQ: ${err.message}`);
    } finally {
      setDeletingId(null);
      setTimeout(() => setStatus(null), 1800);
    }
  };

  return (
    <div className={`faq-page ${adminMode ? 'admin-active' : ''}`}>
      <NavBar adminMode={adminMode} onAdminLogout={onAdminLogout} isAdminLoggedIn={isAdminLoggedIn} />

      <main className="faq-shell">
        <section className="faq-hero">
          <p className="faq-eyebrow">FAQ</p>
          <h1>Vanliga frågor och svar</h1>
          <p className="faq-lead">
            Här hittar du snabba svar på de vanligaste frågorna om hur vi jobbar, vår onboarding och våra tjänster.
          </p>
        </section>

        {adminMode && (
          <section className="faq-admin-card">
            <h2>Lägg till ny fråga</h2>
            <form className="faq-admin-form" onSubmit={handleCreate}>
              <label>
                Fråga
                <input
                  type="text"
                  value={createDraft.question}
                  onChange={(e) => setCreateDraft((prev) => ({ ...prev, question: e.target.value }))}
                  placeholder="Skriv frågan"
                />
              </label>
              <label>
                Svar
                <textarea
                  rows="4"
                  value={createDraft.answer}
                  onChange={(e) => setCreateDraft((prev) => ({ ...prev, answer: e.target.value }))}
                  placeholder="Skriv svaret"
                />
              </label>
              <div className="faq-admin-grid">
                <label>
                  Sortering
                  <input
                    type="number"
                    value={createDraft.sortOrder}
                    onChange={(e) => setCreateDraft((prev) => ({ ...prev, sortOrder: e.target.value }))}
                  />
                </label>
                <label className="faq-checkbox">
                  <input
                    type="checkbox"
                    checked={createDraft.isActive}
                    onChange={(e) => setCreateDraft((prev) => ({ ...prev, isActive: e.target.checked }))}
                  />
                  Visa publikt
                </label>
              </div>
              <button type="submit" disabled={creating}>
                {creating ? 'Sparar...' : 'Skapa FAQ'}
              </button>
            </form>
          </section>
        )}

        {status && <p className="faq-status">{status}</p>}
        {error && <p className="faq-error">Fel: {error}</p>}

        <section className="faq-list" aria-live="polite">
          {loading ? (
            <p className="faq-muted">Laddar frågor...</p>
          ) : visibleItems.length === 0 ? (
            <p className="faq-muted">Inga FAQ-frågor ännu.</p>
          ) : (
            visibleItems.map((item) => {
              const isExpanded = expandedId === item.id;
              const isEditing = editingId === item.id;

              return (
                <article className="faq-item" key={item.id}>
                  <button
                    className={`faq-question ${isExpanded ? 'open' : ''}`}
                    onClick={() => setExpandedId(isExpanded ? null : item.id)}
                  >
                    <span>{item.question || 'Utan rubrik'}</span>
                    <span className="faq-indicator">{isExpanded ? '−' : '+'}</span>
                  </button>

                  {isExpanded && (
                    <div className="faq-answer-wrap">
                      {isEditing ? (
                        <div className="faq-edit-form">
                          <label>
                            Fråga
                            <input
                              type="text"
                              value={editDraft.question}
                              onChange={(e) => setEditDraft((prev) => ({ ...prev, question: e.target.value }))}
                            />
                          </label>
                          <label>
                            Svar
                            <textarea
                              rows="4"
                              value={editDraft.answer}
                              onChange={(e) => setEditDraft((prev) => ({ ...prev, answer: e.target.value }))}
                            />
                          </label>
                          <div className="faq-admin-grid">
                            <label>
                              Sortering
                              <input
                                type="number"
                                value={editDraft.sortOrder}
                                onChange={(e) => setEditDraft((prev) => ({ ...prev, sortOrder: e.target.value }))}
                              />
                            </label>
                            <label className="faq-checkbox">
                              <input
                                type="checkbox"
                                checked={editDraft.isActive}
                                onChange={(e) => setEditDraft((prev) => ({ ...prev, isActive: e.target.checked }))}
                              />
                              Visa publikt
                            </label>
                          </div>
                          <div className="faq-admin-actions">
                            <button type="button" onClick={() => saveEdit(item.id)} disabled={updatingId === item.id}>
                              {updatingId === item.id ? 'Sparar...' : 'Spara'}
                            </button>
                            <button type="button" className="ghost" onClick={() => setEditingId(null)}>
                              Avbryt
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="faq-answer">{item.answer}</p>
                          {adminMode && (
                            <div className="faq-admin-actions">
                              <button type="button" onClick={() => startEdit(item)}>Redigera</button>
                              <button
                                type="button"
                                className="danger"
                                onClick={() => removeItem(item.id)}
                                disabled={deletingId === item.id}
                              >
                                {deletingId === item.id ? 'Tar bort...' : 'Ta bort'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </article>
              );
            })
          )}
        </section>

        <section className="faq-cta-wrap faq-cta-discreet">
          <div>
            <p className="faq-cta-eyebrow">Behöver du mer hjälp?</p>
            <h2>Hittar du inte svaret i FAQ?</h2>
            <p>Skicka ett kort meddelande så återkommer vi snabbt med rätt vägledning.</p>
          </div>
          <a href="/kontakt" className="faq-cta-link">Kontakta oss</a>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default FAQ;
